/**
 * #1157 E2.5 (Ted #32b, his Item 32.7) — a corrected checkout time decides
 * the late pickup on the desk's screen, the same way it now does on the
 * server.
 *
 * Ted: a boarder stays past 11:00 and Gingr adds the $47 daycare charge;
 * "with a package he sometimes removes that charge, adjusts the checkout
 * time, and deducts a package day." The AC: "If I set checkout to 10:50 the
 * $47 charge goes away; if I leave it, I can take a package day instead."
 *
 * The row's late-pickup proposal is read from the server's clock while the
 * dog is still checked in. When the desk types a corrected checkout time on
 * the row, this re-reads that proposal at the corrected time, so the row and
 * the checkout agree with what the server will decide from `checked_out_at`
 * (`latePickupInstant` in payments-admin/service.ts):
 *
 *   • corrected to at or before the cutoff → not late: no charge offered,
 *     and nothing sent with the checkout (the server would refuse it);
 *   • corrected to a time still after the cutoff → late, with the minutes
 *     and the "collected …" time of the CORRECTED pickup.
 *
 * It only ever LOWERS lateness. A proposal the server did not call late is
 * returned unchanged: a corrected time cannot be later than the moment of
 * check-out (the server bounds it), and a screen must never add a charge the
 * server did not propose.
 *
 * Pure: no React, no fetch. Times are read in the FACILITY's zone (the
 * proposal's `timezone`), never the browser's.
 */
import type { AdminLatePickup } from '../../lib/api.js';

/** `HH:MM` → minutes since midnight; null when malformed. */
export function minutesOfLocalTime(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

/** Minutes since local midnight of `iso` in `timeZone`; null when unreadable. */
export function localMinutesAt(iso: string, timeZone: string): number | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(at);
    const h = Number(parts.find((p) => p.type === 'hour')?.value);
    const mi = Number(parts.find((p) => p.type === 'minute')?.value);
    if (!Number.isFinite(h) || !Number.isFinite(mi)) return null;
    return (h % 24) * 60 + mi;
  } catch {
    return null;
  }
}

function hhmm(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Minutes → "10:50 AM", the form the row prints (same as the server's labels). */
export function label12h(minutes: number): string {
  const h24 = Math.floor(minutes / 60);
  const mi = minutes % 60;
  const suffix = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(mi).padStart(2, '0')} ${suffix}`;
}

export interface CorrectedLatePickup {
  /** The proposal to render and act on. */
  readonly proposal: AdminLatePickup | null;
  /**
   * Set when a corrected checkout time took a late pickup back to on time:
   * the corrected time and the cutoff, for the row to say so.
   */
  readonly cleared: { readonly correctedLabel: string; readonly graceLabel: string } | null;
}

/**
 * The proposal as of the corrected checkout time `correctedIso` (an ISO
 * instant, as `correctedInstantToSend` produces it), or unchanged when there
 * is no correction, the proposal is not a late one, or the time cannot be read.
 */
export function applyCorrectedCheckout(
  proposal: AdminLatePickup | null,
  correctedIso: string | null | undefined,
): CorrectedLatePickup {
  const unchanged: CorrectedLatePickup = { proposal, cleared: null };
  if (!proposal || !proposal.applicable || !proposal.late || !correctedIso) return unchanged;
  const at = localMinutesAt(correctedIso, proposal.timezone);
  const grace = minutesOfLocalTime(proposal.graceLocalTime);
  if (at === null || grace === null) return unchanged;
  const correctedLabel = label12h(at);
  if (at <= grace) {
    return {
      proposal: {
        ...proposal,
        late: false,
        minutesPastGrace: 0,
        nowLocalTime: hhmm(at),
        nowLabel: correctedLabel,
      },
      cleared: { correctedLabel, graceLabel: proposal.graceLabel },
    };
  }
  return {
    proposal: {
      ...proposal,
      minutesPastGrace: at - grace,
      nowLocalTime: hhmm(at),
      nowLabel: correctedLabel,
    },
    cleared: null,
  };
}
